import { useEffect, useState } from 'react'
import { paperAPI } from '../../services/api'
import type { PaperRegime } from '../../types'

export const PAPER_REGIME_POLL_MS = 300_000

const usd = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

/**
 * Is the strategy's BTC trend filter on right now? Same daily series and rule
 * as the paper run: last closed daily above its 200-day SMA.
 */
const PaperRegimeBadge = () => {
  const [regime, setRegime] = useState<PaperRegime | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const data = await paperAPI.regime()
        if (!active) return
        setRegime(data)
        setFailed(false)
      } catch (error) {
        if (!active) return
        console.error('Paper regime refresh failed:', error)
        setFailed(true)
      }
    }
    load()
    const timer = setInterval(load, PAPER_REGIME_POLL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [])

  if (!regime) {
    return (
      <div className="flex min-h-[28px] items-center text-xs text-gray-500" data-testid="paper-regime">
        {failed ? 'BTC trend filter unavailable' : null}
      </div>
    )
  }

  const on = regime.regime_on
  const label =
    regime.sma_200 == null
      ? 'BTC 200-day average warming up — strategy waiting'
      : on
      ? 'BTC above 200-day — strategy active'
      : 'BTC below 200-day — strategy waiting'

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="paper-regime">
      <span
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
          on
            ? 'border-green-500/40 bg-green-500/10 text-green-300'
            : 'border-amber-500/40 bg-amber-500/10 text-amber-300'
        }`}
        title={`The filter is judged on the last closed daily candle (${usd(regime.last_close)} on ${
          regime.last_close_date
        }) and re-checked by the run after each 00:00 UTC close.`}
        data-testid="paper-regime-badge"
      >
        <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-green-400' : 'bg-amber-400'}`} aria-hidden />
        {label}
      </span>
      <span className="font-mono text-xs tabular-nums text-gray-400" data-testid="paper-regime-figures">
        BTC <span className="text-gray-200">{usd(regime.current_price)}</span>
        <span className="mx-2 text-gray-600">·</span>
        200D SMA <span className="text-gray-200">{regime.sma_200 != null ? usd(regime.sma_200) : '—'}</span>
        {regime.stale && <span className="ml-2 text-gray-500">(stale)</span>}
      </span>
    </div>
  )
}

export default PaperRegimeBadge
